/**
 * Scenarios for the web-hacking track practicals (Modules 3-14), read by
 * WebLab.jsx. Each is a short, safe, in-browser understand-and-recognise
 * exercise: read the situation, answer how it works and how to stop it.
 * No live target, no payload, no tool - the recognise-not-perform stance
 * the whole course holds, in plain English for age 11+.
 *
 * Shape: id -> { title, moduleLabel, lessonSlug, outro, stages:[...] }.
 * A stage is finished when every item is answered correctly.
 */

export const WEB_LABS = {
  /* ---------- Module 3: How the Web Works ---------- */
  'how-web-works': {
    title: 'Inspect a web request',
    moduleLabel: 'Ethical Hacker, Module 3',
    lessonSlug: 'ww1-how-the-web-works',
    outro: 'You can read the round trip now — a request out, a response back.',
    stages: [
      {
        id: 's1', label: 'Read the request', heading: 'Read the request',
        lead: 'Here is what your browser sent when it asked for a page. Read it, then answer.',
        tutorIdle: 'The first line has two parts: the method (what to do) and the path (what to ask for). The lines under it are headers — extra notes about the request.',
        tutorDone: 'That is the whole ask: a method, a path, and some headers. The reply comes back with a status code first of all.',
        doneNote: 'You can read a request.',
        scenario: [
          '> GET /shop/cart HTTP/1.1',
          '> Host: example-shop.com',
          '> Cookie: session=8f2a...',
        ],
        items: [
          { id: 'i1', icon: '📤', q: 'What is "GET" here?', options: ['The website name', 'The method — it fetches a page', 'A password'], answer: 1, why: 'GET means "fetch this". POST means "send this". The method says what kind of request it is.' },
          { id: 'i2', icon: '🍪', q: 'What is the Cookie line doing?', options: ['Storing a biscuit recipe', 'Carrying a note so the site remembers you', 'Hiding the page'], answer: 1, why: 'The web forgets you between clicks, so your browser carries a cookie back each time to stay logged in.' },
        ],
      },
      {
        id: 's2', label: 'Read the reply', heading: 'Read the reply',
        lead: 'The server answered. The first thing it sends is a status code — how it went.',
        tutorIdle: '200 means all good. 404 means not found. 500 means the server broke. 301 or 302 means "go somewhere else".',
        tutorDone: 'Status codes are the quick summary of every reply. Learn a handful and you can read the web.',
        scenario: [
          '> HTTP/1.1 200 OK',
          '> Content-Type: text/html',
        ],
        items: [
          { id: 'i3', icon: '📥', q: 'What does "200 OK" tell you?', options: ['The page is 200 pages long', 'It worked — here is the result', 'You are blocked'], answer: 1, why: '200 is the "it worked" code. The content of the page follows it.' },
          { id: 'i4', icon: '🔒', q: 'A padlock (HTTPS) on a site means…', options: ['The site is honest and safe', 'The connection is private and the site owns its name', 'The site is free'], answer: 1, why: 'HTTPS scrambles the connection and proves the site owns its name. It does not promise the site is honest.' },
        ],
      },
    ],
  },

  /* ---------- Module 4: Web Building Blocks ---------- */
  'building-blocks': {
    title: 'Look inside a login page',
    moduleLabel: 'Ethical Hacker, Module 4',
    lessonSlug: 'wb1-web-building-blocks',
    outro: 'HTML shows it, CSS styles it, JavaScript moves it — and none of it can be trusted by the server.',
    stages: [
      {
        id: 's1', label: 'The three parts', heading: 'The three parts of a page',
        lead: 'A page is built from three things working together. Match each to its job.',
        tutorIdle: 'HTML is the bones — the boxes and text. CSS is the paint — colours and layout. JavaScript is the movement — it reacts to clicks.',
        tutorDone: 'Bones, paint, movement. Every page you ever open is those three.',
        doneNote: 'You know the three building blocks.',
        items: [
          { id: 'i1', icon: '🦴', q: 'Which one holds the words and boxes of a page?', options: ['CSS', 'HTML', 'JavaScript'], answer: 1, why: 'HTML is the structure — the headings, forms and boxes.' },
          { id: 'i2', icon: '🎨', q: 'Which one decides the colours and layout?', options: ['CSS', 'HTML', 'JavaScript'], answer: 0, why: 'CSS is the styling — how everything looks.' },
        ],
      },
      {
        id: 's2', label: 'Where the data hides', heading: 'Where the login data goes',
        lead: 'A login form sends what you typed. Some of it is easy to see, and that matters.',
        tutorIdle: 'A form has fields. When you press submit, the browser packs them into a request and sends them. A "hidden" field is still in the page — anyone can read or change it.',
        tutorDone: 'The lesson: everything in the page runs on the user’s machine. A hidden field is not a secret.',
        scenario: [
          '> <form method="POST" action="/login">',
          '>   username: [ priya      ]',
          '>   password: [ ******     ]',
          '>   <input type="hidden" name="price" value="10">',
          '> </form>',
        ],
        items: [
          { id: 'i3', icon: '🕵️', q: 'Can a visitor see and change that hidden "price" field?', options: ['No, it is hidden', 'Yes — it is in the page, which is on their machine', 'Only the admin can'], answer: 1, why: 'Hidden only means not drawn on screen. The whole page lives on the user’s computer, so they can read and edit it.' },
          { id: 'i4', icon: '🛡️', q: 'So where must the real price be checked?', options: ['In the hidden field', 'On the server, after the request arrives', 'In the CSS'], answer: 1, why: 'The only check that counts is the one the server does itself. Never trust a value the browser sent.' },
        ],
      },
    ],
  },

  /* ---------- Module 5: Web Reconnaissance ---------- */
  recon: {
    title: 'Recon a practice site',
    moduleLabel: 'Ethical Hacker, Module 5',
    lessonSlug: 'wr1-web-reconnaissance',
    outro: 'Most of an attack is just careful reading. So is most of the defence.',
    stages: [
      {
        id: 's1', label: 'Read what is public', heading: 'Read what the site tells you',
        lead: 'Before anything else, an ethical hacker reads. Websites give a lot away for free.',
        tutorIdle: 'Reconnaissance means looking, not touching. Page source, file names, error messages and old pages all leak clues about how a site is built.',
        tutorDone: 'None of that was an attack. It was reading — and it is where every real test starts.',
        doneNote: 'You have read the surface.',
        scenario: [
          '> Page footer: "Powered by ShopCart v1.2"',
          '> A link points to /admin/login',
          '> An error page shows a file path: /var/www/shop/db.php',
        ],
        items: [
          { id: 'i1', icon: '🔎', q: 'Why is "Powered by ShopCart v1.2" useful to a tester?', options: ['It is just decoration', 'It names the software and version, so known weaknesses can be looked up', 'It speeds up the site'], answer: 1, why: 'Knowing the exact software and version lets a tester (or attacker) check whether that version has known holes.' },
          { id: 'i2', icon: '🚪', q: 'What does finding /admin/login tell you?', options: ['Nothing', 'Where the staff door is — worth protecting well', 'The site is broken'], answer: 1, why: 'Recon maps the attack surface: every door, including the staff one. Defenders use the same map to lock them.' },
        ],
      },
      {
        id: 's2', label: 'Shrink the surface', heading: 'Make the map smaller',
        lead: 'Every clue the site leaks is something a defender can take away. Which of these helps?',
        tutorIdle: 'You cannot patch a careers page or a footer — but you can choose what it says. The best fix is often to simply not publish the clue.',
        tutorDone: 'Attack surface is a choice. The less you reveal, the less there is to aim at.',
        items: [
          { id: 'i3', icon: '🧹', q: 'A defender wants to leak less. What helps most?', options: ['Hide the version number and stop error pages showing file paths', 'Change the site colours', 'Make the logo bigger'], answer: 0, why: 'Version banners and detailed error messages are free intelligence for an attacker. Turning them off shrinks the surface.' },
          { id: 'i4', icon: '🗺️', q: 'Is reconnaissance itself an attack?', options: ['Yes, always', 'No — it is reading public information; the attack, if any, comes later', 'Only on weekends'], answer: 1, why: 'Recon is looking at what is already public. It is the planning stage, not the break-in.' },
        ],
      },
    ],
  },

  /* ---------- Module 6: Authentication & Sessions ---------- */
  auth: {
    title: 'Test a login system',
    moduleLabel: 'Ethical Hacker, Module 6',
    lessonSlug: 'as1-authentication-and-sessions',
    outro: 'A login is only as strong as how it stores secrets and how it remembers you.',
    stages: [
      {
        id: 's1', label: 'How logins fail', heading: 'How a login gets weak',
        lead: 'A simulated login system. Spot what makes it easy to break into.',
        tutorIdle: 'Authentication is proving who you are. It gets weak when passwords are short, when there is no limit on guesses, or when the "remember me" note is easy to copy.',
        tutorDone: 'Long passwords, a limit on guesses, and a session ID nobody can guess. Those three carry most of it.',
        doneNote: 'You can spot a weak login.',
        items: [
          { id: 'i1', icon: '🔓', q: 'The login lets you try passwords as many times as you like. Why is that bad?', options: ['It is fine', 'A computer can try millions of guesses until one works', 'It uses more electricity'], answer: 1, why: 'With no limit, an attacker can guess passwords automatically. A lock-out after a few tries stops this.' },
          { id: 'i2', icon: '🔑', q: 'Best way to store users’ passwords?', options: ['In plain text', 'As a salted hash — a one-way fingerprint', 'In the page source'], answer: 1, why: 'Store a hash, never the password itself. Then even a stolen database does not hand over the passwords.' },
        ],
      },
      {
        id: 's2', label: 'Sessions', heading: 'The note that keeps you logged in',
        lead: 'After you log in, the site gives you a session ID in a cookie. It matters a lot.',
        tutorIdle: 'A session ID is like a wristband at a fair — show it and you are let in. If it is easy to guess, or someone copies it, they become you.',
        tutorDone: 'Long random session IDs, sent only over HTTPS, and cleared on logout. That is a safe wristband.',
        items: [
          { id: 'i3', icon: '🎟️', q: 'A session ID counts up: user 1 gets 1001, user 2 gets 1002. What is the risk?', options: ['None', 'Anyone can guess other people’s IDs and become them', 'It is too long'], answer: 1, why: 'Guessable session IDs let someone step into another account. IDs must be long and random.' },
          { id: 'i4', icon: '🚪', q: 'What should logout do to the session?', options: ['Nothing', 'Throw the session away on the server, not just hide the cookie', 'Change the page colour'], answer: 1, why: 'Real logout kills the session on the server. Otherwise a copied wristband still works after "logging out".' },
        ],
      },
    ],
  },

  /* ---------- Module 7: SQL Injection ---------- */
  sqli: {
    title: 'Watch a query break',
    moduleLabel: 'Ethical Hacker, Module 7',
    lessonSlug: 'si1-sql-injection',
    outro: 'The fix was never to block clever words. It was to keep the question and the answer apart.',
    stages: [
      {
        id: 's1', label: 'Why it breaks', heading: 'Why a query breaks',
        lead: 'A site glues what you type straight into a database question. Watch what that allows.',
        tutorIdle: 'The database is told: find the pupil whose name is [what you typed]. If your typing becomes part of the instruction instead of just the name, you have changed the question.',
        tutorDone: 'That is SQL injection: typing that stops being data and turns into command. It happens when the two are mixed.',
        doneNote: 'You see why it breaks.',
        scenario: [
          '> The site runs: SELECT * FROM pupils WHERE name = ‘[your typing]’',
          '> Type an ordinary name and it finds that pupil.',
          '> Type a name with a stray quote and the sentence falls apart.',
        ],
        items: [
          { id: 'i1', icon: '💉', q: 'Where does the problem come from?', options: ['The database is broken', 'Your typing is glued into the middle of the instruction', 'The internet is slow'], answer: 1, why: 'When user input is pasted straight into a command, the input can change the command. That is the root of injection.' },
          { id: 'i2', icon: '🧩', q: 'What is really being trusted that should not be?', options: ['The screen', 'Whatever the visitor typed', 'The colour of the button'], answer: 1, why: 'The server trusted the visitor’s text as part of its own instruction. The browser is the visitor’s machine — never trust it.' },
        ],
      },
      {
        id: 's2', label: 'The real fix', heading: 'The fix that actually works',
        lead: 'People try to block bad words. That is a losing game. There is a proper fix.',
        tutorIdle: 'Blocking quotes and words never ends — there is always another spelling. The real fix keeps the instruction and the data in separate envelopes so typing can never become command.',
        tutorDone: 'Parameters — separate envelopes for the question and the answer — end the whole class of bug, not one trick at a time.',
        items: [
          { id: 'i3', icon: '🛠️', q: 'Which fix actually stops SQL injection?', options: ['Ban the word "select"', 'Use parameters, so input is always data and never command', 'Hide the search box'], answer: 1, why: 'Parameterised queries keep the instruction and the input apart. The input cannot become part of the command.' },
          { id: 'i4', icon: '🚫', q: 'Why is "just block dangerous characters" a weak fix?', options: ['It is not weak', 'There is always another way to write the same thing', 'It costs money'], answer: 1, why: 'Filters can be slipped past with different spellings. Separating command from data cannot be slipped past.' },
        ],
      },
    ],
  },

  /* ---------- Module 8: Cross-Site Scripting (XSS) ---------- */
  xss: {
    title: 'Spot the script that runs',
    moduleLabel: 'Ethical Hacker, Module 8',
    lessonSlug: 'xs1-cross-site-scripting',
    outro: 'If a site shows your words back without cleaning them, your words can become its code.',
    stages: [
      {
        id: 's1', label: 'How XSS happens', heading: 'When words become code',
        lead: 'A site puts a visitor’s comment straight onto the page. See what that lets in.',
        tutorIdle: 'A browser cannot tell the difference between text the site meant and text a visitor sneaked in — unless the site cleans it first. Uncleaned input can carry a little program.',
        tutorDone: 'That is XSS: getting the browser to run code that came from a visitor, because the site never cleaned the input.',
        doneNote: 'You see how XSS gets in.',
        scenario: [
          '> A comment box saves whatever you type.',
          '> The page shows every comment back to everyone.',
          '> One comment contains bits that look like page code, not words.',
        ],
        items: [
          { id: 'i1', icon: '🟧', q: 'Why can a comment turn into running code?', options: ['Browsers are broken', 'The site showed the visitor’s text without cleaning it first', 'Comments are always dangerous'], answer: 1, why: 'If input is shown as-is, code hidden in it runs in other people’s browsers. Cleaning (escaping) input stops that.' },
          { id: 'i2', icon: '👥', q: 'A "stored" XSS comment is dangerous to whom?', options: ['Only the person who wrote it', 'Everyone who later opens the page', 'Nobody'], answer: 1, why: 'Stored XSS sits in the page and runs for every visitor after — which is what makes it worse than a one-off.' },
        ],
      },
      {
        id: 's2', label: 'The fix', heading: 'How to shut it',
        lead: 'The fix is the same idea as before: do not trust what came from the visitor.',
        tutorIdle: 'Escape output so a visitor’s text is always shown as text, never run as code. A Content Security Policy adds a second wall.',
        tutorDone: 'Clean the output, and a comment stays a comment. That is the whole defence.',
        items: [
          { id: 'i3', icon: '🧼', q: 'What is the main fix for XSS?', options: ['Turn off comments forever', 'Escape output so visitor text is shown as text, not run as code', 'Make the font bigger'], answer: 1, why: 'Escaping turns dangerous characters into harmless display characters, so nothing a visitor typed can run.' },
          { id: 'i4', icon: '🧱', q: 'What extra wall helps if something slips through?', options: ['A Content Security Policy that limits what code may run', 'A louder alarm sound', 'A nicer logo'], answer: 0, why: 'A CSP tells the browser which code is allowed, so sneaked-in code is blocked even if it reaches the page.' },
        ],
      },
    ],
  },

  /* ---------- Module 9: Access Control & Authorization ---------- */
  access: {
    title: 'Whose data is it?',
    moduleLabel: 'Ethical Hacker, Module 9',
    lessonSlug: 'ac1-access-control',
    outro: 'Logging in says who you are. Access control decides what you are allowed to touch.',
    stages: [
      {
        id: 's1', label: 'The number trick', heading: 'Changing a number in the address',
        lead: 'You are logged in as yourself. Your order is at /order/1001. Watch what happens.',
        tutorIdle: 'This is IDOR — insecure direct object reference. If the site shows order 1002 just because you asked for it, it never checked that the order is yours.',
        tutorDone: 'The site checked that you were logged in, but not that the thing was yours. Those are two different checks.',
        doneNote: 'You spotted the missing check.',
        scenario: [
          '> You open /order/1001 — it is your order. Fine.',
          '> You change it to /order/1002 and press enter.',
          '> Someone else’s order appears.',
        ],
        items: [
          { id: 'i1', icon: '🔢', q: 'What went wrong?', options: ['You hacked the database', 'The site never checked the order belongs to you', 'The internet glitched'], answer: 1, why: 'Being logged in is not the same as being allowed. The server must check the item belongs to you, every time.' },
          { id: 'i2', icon: '🧑‍⚖️', q: 'What is the difference between authentication and authorization?', options: ['They are the same', 'Authentication is WHO you are; authorization is WHAT you may do', 'One is faster'], answer: 1, why: 'Authentication proves identity. Authorization decides access. A site needs both, checked separately.' },
        ],
      },
      {
        id: 's2', label: 'Doing it right', heading: 'How to check properly',
        lead: 'Where should the "is this allowed?" check live? Judge these.',
        tutorIdle: 'Hiding a button does not remove the page behind it. The only check that counts is on the server, for every request.',
        tutorDone: 'Check ownership on the server, every request. Hiding things in the browser is decoration, not a lock.',
        items: [
          { id: 'i3', icon: '🙈', q: 'A site hides the "delete" button from normal users. Is that enough?', options: ['Yes', 'No — the request behind it still works unless the server checks', 'Only on phones'], answer: 1, why: 'Hiding a button changes the look, not the lock. The server must refuse the action for people who are not allowed.' },
          { id: 'i4', icon: '👑', q: 'What is "privilege escalation"?', options: ['Getting a bigger screen', 'A normal user finding a way to act like an admin', 'Logging out'], answer: 1, why: 'Privilege escalation is climbing from your own rights up to someone with more — the thing good access control prevents.' },
        ],
      },
    ],
  },

  /* ---------- Module 10: CSRF & Web Attacks ---------- */
  csrf: {
    title: 'The request you did not mean to send',
    moduleLabel: 'Ethical Hacker, Module 10',
    lessonSlug: 'cs1-csrf-and-web-attacks',
    outro: 'A token the attacker cannot guess is what proves a request really came from you.',
    stages: [
      {
        id: 's1', label: 'The trick', heading: 'A request in your name',
        lead: 'You are logged into your bank in one tab. Another site tries something. Read on.',
        tutorIdle: 'CSRF means cross-site request forgery. Your browser sends your bank cookie with every request to the bank — even one started by a different, sneaky site.',
        tutorDone: 'The bank saw your cookie and believed the request was yours. It could not tell who really started it.',
        doneNote: 'You see the trick.',
        scenario: [
          '> You are logged into your bank in Tab 1.',
          '> Tab 2 is a game site that secretly tells your browser to ask the bank to transfer money.',
          '> Your browser attaches your bank cookie automatically.',
        ],
        items: [
          { id: 'i1', icon: '🛡️', q: 'Why did the bank trust the sneaky request?', options: ['The attacker knew your password', 'Your browser sent your bank cookie with it automatically', 'The bank was offline'], answer: 1, why: 'Cookies ride along on every request to that site. The bank saw a valid cookie and assumed you meant it.' },
          { id: 'i2', icon: '🎭', q: 'Did the attacker ever see your password?', options: ['Yes', 'No — they never needed it; they borrowed your logged-in browser', 'Only the first letter'], answer: 1, why: 'CSRF needs no password. It rides on the fact that you are already logged in.' },
        ],
      },
      {
        id: 's2', label: 'The defence', heading: 'How to prove it was really you',
        lead: 'The site needs a way to tell a real request from a forged one. Judge these.',
        tutorIdle: 'A secret token the real page includes — and a sneaky site cannot know — proves the request came from your genuine page. SameSite cookies help too.',
        tutorDone: 'A per-page secret token the attacker cannot guess is the core fix. SameSite cookies back it up.',
        items: [
          { id: 'i3', icon: '🎫', q: 'What stops CSRF?', options: ['A secret token in the real form that a sneaky site cannot know', 'A bigger password', 'Turning off cookies for everyone'], answer: 0, why: 'The genuine page carries a random token the server checks. A forged request has no way to include it.' },
          { id: 'i4', icon: '🍪', q: 'What does a "SameSite" cookie do?', options: ['Tastes better', 'Tells the browser not to send the cookie on requests from other sites', 'Makes the page load faster'], answer: 1, why: 'SameSite cookies are held back on cross-site requests, so the forged request arrives without the login cookie.' },
        ],
      },
    ],
  },

  /* ---------- Module 11: API Hacking ---------- */
  api: {
    title: 'Poke at an API',
    moduleLabel: 'Ethical Hacker, Module 11',
    lessonSlug: 'ah1-api-hacking',
    outro: 'An API is just the website’s back door for data — and it needs the same locks as the front.',
    stages: [
      {
        id: 's1', label: 'What an API is', heading: 'The data door',
        lead: 'Apps talk to servers through APIs. Read this reply and answer.',
        tutorIdle: 'An API sends plain data (often JSON) instead of a full page. Same idea as a normal request — a method, a path (endpoint), and a reply — just data, not decoration.',
        tutorDone: 'An API is the same round trip, returning data. Everything you learned about requests still applies.',
        doneNote: 'You can read an API reply.',
        scenario: [
          '> GET /api/users/1001',
          '> Reply: { "id": 1001, "name": "Priya", "role": "user" }',
        ],
        items: [
          { id: 'i1', icon: '🔌', q: 'What is /api/users/1001?', options: ['A picture', 'An endpoint — the address of one user’s data', 'A password'], answer: 1, why: 'An endpoint is a path that returns a specific piece of data. Here, the user with id 1001.' },
          { id: 'i2', icon: '📦', q: 'What is the reply written in?', options: ['JSON — a simple way to write data', 'A photo', 'A song'], answer: 0, why: 'JSON is a plain text way to write data as names and values. Most APIs speak it.' },
        ],
      },
      {
        id: 's2', label: 'The same old bugs', heading: 'APIs forget the locks',
        lead: 'APIs often skip checks the website itself remembers. Judge these.',
        tutorIdle: 'Because APIs feel "behind the scenes", people forget to check who is asking. Changing the id in the address is the same trick as before.',
        tutorDone: 'An API needs the same access checks as any page. "Hidden" is not "protected".',
        items: [
          { id: 'i3', icon: '🔢', q: 'You change /api/users/1001 to /api/users/1002 and get someone else’s data. What is missing?', options: ['Nothing', 'A check that you are allowed to see that user', 'A faster connection'], answer: 1, why: 'Same as IDOR: the API must check the data belongs to you, not just return whatever id you ask for.' },
          { id: 'i4', icon: '🗝️', q: 'How should an API know who is asking?', options: ['It should not check', 'Every request must prove who it is, e.g. with a token', 'By the time of day'], answer: 1, why: 'Each API request must carry proof of identity and be checked. Being an API is not a disguise the server should trust.' },
        ],
      },
    ],
  },

  /* ---------- Module 12: Web Hacking Tools ---------- */
  tools: {
    title: 'Read the traffic',
    moduleLabel: 'Ethical Hacker, Module 12',
    lessonSlug: 'wt1-web-hacking-tools',
    outro: 'The tools only show you the requests you were already sending. Understanding them is the skill.',
    stages: [
      {
        id: 's1', label: 'What the tools do', heading: 'What a proxy shows you',
        lead: 'Testers use tools that sit between the browser and the site. What are they for?',
        tutorIdle: 'Browser DevTools and a proxy let you watch every request go by, pause one, and look at exactly what was sent and returned. Nothing magic — just a clear window on the traffic.',
        tutorDone: 'The tools are a magnifying glass on your own requests. The learning is in reading what you see.',
        doneNote: 'You know what the tools are for.',
        items: [
          { id: 'i1', icon: '🔍', q: 'What do browser DevTools let you see?', options: ['The future', 'Every request and reply your browser makes', 'Other people’s screens'], answer: 1, why: 'The Network tab lists every request and its reply — the same view a lot of web testing starts from.' },
          { id: 'i2', icon: '⏸️', q: 'What does a proxy let a tester do?', options: ['Pause a request and look at exactly what is being sent', 'Speed up the internet', 'Delete a website'], answer: 0, why: 'A proxy sits in the middle so you can inspect, and with permission modify, the traffic you send.' },
        ],
      },
      {
        id: 's2', label: 'Using them well', heading: 'The rule that comes with the tools',
        lead: 'These tools are powerful. One rule matters more than any feature.',
        tutorIdle: 'A tool that can change your requests can be pointed at anything. The line between a tester and a criminal is permission — a written OK to test a specific target.',
        tutorDone: 'Permission first, always. The tool is the easy part; the authorisation is what makes it ethical.',
        items: [
          { id: 'i3', icon: '📝', q: 'Before testing any site with these tools, you need…', options: ['A fast laptop', 'Clear permission to test that specific site', 'A nickname'], answer: 1, why: 'Testing without permission is not ethical hacking — it is an offence. A scope or written authorisation comes first.' },
          { id: 'i4', icon: '🧰', q: 'Does the tool find bugs for you?', options: ['Yes, automatically and perfectly', 'No — it shows you the traffic; understanding it is the skill', 'Only on Fridays'], answer: 1, why: 'Tools reveal the requests. Knowing what is wrong with them is the part that takes learning.' },
        ],
      },
    ],
  },

  /* ---------- Module 13: Web Vulnerability Hunting ---------- */
  hunting: {
    title: 'Put it together',
    moduleLabel: 'Ethical Hacker, Module 13',
    lessonSlug: 'vh1-web-vulnerability-hunting',
    outro: 'Real hunting is recon, requests and known bugs, used together, patiently.',
    stages: [
      {
        id: 's1', label: 'Chain the steps', heading: 'One bug leads to the next',
        lead: 'Real testing combines everything so far. Put this little hunt in the right order of thinking.',
        tutorIdle: 'You read first (recon), then you try the ordinary features while watching the traffic, then you test the weak spots you found. Each step feeds the next.',
        tutorDone: 'Look, then poke, then dig. Hunting is those three on repeat, writing down what you find.',
        doneNote: 'You can plan a hunt.',
        items: [
          { id: 'i1', icon: '🧭', q: 'What comes first in a real test?', options: ['Guess passwords at random', 'Recon — read and map the site', 'Give up'], answer: 1, why: 'You cannot test what you have not mapped. Reading and mapping the site comes before anything else.' },
          { id: 'i2', icon: '🔗', q: 'You find the site echoes your name onto the page. Which earlier lesson does that connect to?', options: ['CSS colours', 'XSS — input shown without cleaning', 'Faster wifi'], answer: 1, why: 'Recognising a known weakness (here, possible XSS) is exactly how the earlier modules pay off in a real hunt.' },
        ],
      },
      {
        id: 's2', label: 'Report it well', heading: 'Finding it is half the job',
        lead: 'An ethical hunter’s job ends in a clear report, not a broken site. Judge these.',
        tutorIdle: 'A good finding says what the bug is, how to see it, and why it matters — so it can be fixed. Breaking things or peeking at real data is not the job.',
        tutorDone: 'Clear, kind, fixable. A good report is what separates helping from harm.',
        items: [
          { id: 'i3', icon: '📄', q: 'What does a good vulnerability report include?', options: ['Just "your site is bad"', 'What the bug is, the steps to see it, and why it matters', 'Nothing, keep it secret'], answer: 1, why: 'A fixable report explains the problem, how to reproduce it, and the impact — so the owner can put it right.' },
          { id: 'i4', icon: '🛑', q: 'You find a real bug on a site you are allowed to test. What now?', options: ['Take all the data to prove it', 'Stop, note it, and report it', 'Tell your friends to try'], answer: 1, why: 'Prove the bug exists, then stop and report. Going further — taking data or telling others — crosses the line.' },
        ],
      },
    ],
  },

  /* ---------- Module 14: Web Hacking CTF ---------- */
  ctf: {
    title: 'The final challenge',
    moduleLabel: 'Ethical Hacker, Module 14',
    lessonSlug: 'ct1-web-hacking-ctf',
    outro: 'A whole methodology, in order — and always inside permission.',
    stages: [
      {
        id: 's1', label: 'The method', heading: 'The whole method, in order',
        lead: 'A CTF is a safe, made-for-practice target. Show you know the method from start to end.',
        tutorIdle: 'CTF means Capture The Flag: a deliberately weak site built for you to practise on, legally. The method is the same every time: map, probe, exploit safely, and note the flag.',
        tutorDone: 'Map, probe, test, record. The same order every time — that steadiness is what a methodology is.',
        doneNote: 'You have the method.',
        items: [
          { id: 'i1', icon: '🏁', q: 'What is a CTF?', options: ['A real company to break into', 'A safe practice site built on purpose for learning', 'A type of cookie'], answer: 1, why: 'A CTF is a legal, made-for-practice target. It is where you use these skills without harming anyone.' },
          { id: 'i2', icon: '📋', q: 'What is a "methodology"?', options: ['A lucky guess', 'A steady order of steps you follow every time', 'A kind of password'], answer: 1, why: 'Methodology is the repeatable order — recon, then requests, then known weaknesses — that stops testing being random.' },
        ],
      },
      {
        id: 's2', label: 'Stay legal', heading: 'The line that never moves',
        lead: 'The most important part of the whole course is not a technique. Judge these.',
        tutorIdle: 'Every skill here is legal on a target you are allowed to test, and an offence on one you are not. Permission is the whole difference between an ethical hacker and a criminal.',
        tutorDone: 'Permission, always. That single rule is what the badge really stands for.',
        items: [
          { id: 'i3', icon: '⚖️', q: 'What makes the difference between an ethical hacker and a criminal?', options: ['The keyboard', 'Permission and intent — testing what you are allowed to, to help', 'The time of day'], answer: 1, why: 'The exact same actions are lawful with permission and an offence without. Permission and good intent are the line.' },
          { id: 'i4', icon: '🎯', q: 'You want to practise these skills. Where is it safe?', options: ['Any website you find', 'On CTFs and labs made for practice, or sites you own or are asked to test', 'Your school’s website, secretly'], answer: 1, why: 'Practise only where you are allowed: purpose-built CTFs, your own sites, or ones you have written permission to test.' },
        ],
      },
    ],
  },
};
